// src/components/SunatIcon.jsx
import { useState } from "react";
import axios from "axios";
import { useDialog } from "./AdminConfirmDialogProvider";
import { Dialog, DialogTitle, Button, useMediaQuery, TextField, Box, Typography, SvgIcon } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Sunat01Icon from '../../assets/images/sunat0.png'; //Azul
import Sunat03Icon from '../../assets/images/sunat9.png'; //Granate
import TaskAltIcon from "@mui/icons-material/TaskAlt";   
import CodeIcon from '@mui/icons-material/Code';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import palette from '../../theme/palette';

const modalPaperSx = {
  m: { xs: 1.5, sm: 2 },
  mt: { xs: 1.5, sm: 5 },
  width: { xs: "calc(100vw - 24px)", sm: 420 },
  maxWidth: "calc(100vw - 24px)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  backgroundColor: palette.surface,
  color: palette.text,
  border: `1px solid ${palette.border}`,
  borderRadius: 2,
  boxShadow: "0 18px 48px rgba(0,0,0,0.34)",
  overflow: "hidden",
};

const createDownloadButtonSx = ({ accent, bg, border, hoverBg, hoverBorder }) => ({
  width: 270,
  height: 42,
  justifyContent: "center",
  borderRadius: 2,
  backgroundColor: bg,
  border,
  color: "rgba(255,255,255,0.90)",
  boxShadow: "none",
  fontSize: "12px",
  fontWeight: 500,
  textAlign: "center",
  position: "relative",
  "& .MuiButton-startIcon": {
    position: "absolute",
    left: 16,
    m: 0,
    color: accent,
  },
  "& .MuiButton-startIcon > *:nth-of-type(1)": {
    fontSize: 22,
  },
  "&:hover": {
    backgroundColor: hoverBg,
    borderColor: hoverBorder,
    boxShadow: "none",
  },
});

// Colores discretos por tipo de documento: tecnico, validacion y representacion impresa.
const xmlButtonSx = createDownloadButtonSx({
  accent: "#8fc7ff",
  bg: "rgba(96,165,250,0.08)",
  border: "1px solid rgba(96,165,250,0.20)",
  hoverBg: "rgba(96,165,250,0.13)",
  hoverBorder: "rgba(143,199,255,0.30)",
});

const cdrButtonSx = createDownloadButtonSx({
  accent: "#7ddbd3",
  bg: "rgba(42,161,152,0.10)",
  border: "1px solid rgba(42,161,152,0.24)",
  hoverBg: "rgba(42,161,152,0.15)",
  hoverBorder: "rgba(125,219,211,0.34)",
});

const pdfButtonSx = createDownloadButtonSx({
  accent: "#f2c185",
  bg: "rgba(242,193,133,0.08)",
  border: "1px solid rgba(242,193,133,0.20)",
  hoverBg: "rgba(242,193,133,0.13)",
  hoverBorder: "rgba(242,193,133,0.30)",
});

const phoneFieldSx = {
  width: 270,
  "& .MuiOutlinedInput-root": {
    height: 40,
    color: palette.text,
    backgroundColor: "rgba(26,33,39,0.48)",
    borderRadius: 2,
    "& fieldset": { borderColor: "rgba(139,154,165,0.14)" },
    "&:hover fieldset": { borderColor: "rgba(42,161,152,0.28)" },
    "&.Mui-focused fieldset": { borderColor: "rgba(42,161,152,0.45)" },
  },
  "& input": {
    color: palette.text,
    fontSize: "13px",
    textAlign: "center",
  },
  "& input::placeholder": {
    color: palette.muted,
    opacity: 1,
  },
};

const closeButtonSx = {
  width: 270,
  height: 40,
  justifyContent: "center",
  borderRadius: 2,
  backgroundColor: "rgba(139,154,165,0.10)",
  border: "1px solid rgba(139,154,165,0.16)",
  color: palette.text,
  boxShadow: "none",
  fontSize: "12px",
  fontWeight: 500,
  "&:hover": {
    backgroundColor: "rgba(139,154,165,0.16)",
    boxShadow: "none",
  },
};

const downloadInfoSx = {
  width: 270,
  mt: 0.75,
  color: palette.muted,
  textAlign: "center",
  fontSize: "11px",
  fontWeight: 500,
  lineHeight: 1.45,
};

const comprobanteInfoSx = {
  width: 270,
  mt: 1.5,
  color: "rgba(255,255,255,0.96)",
  textAlign: "center",
  fontSize: "13px",
  fontWeight: 600,
  lineHeight: 1.25,
};

// Icono PDF propio: version coral, redondeada y legible para boton compacto.
function PdfFileIcon(props) {
  return (
    <SvgIcon viewBox="0 0 64 64" {...props}>
      <path d="M14 5h31l9 9v40c0 4.2-2.8 7-7 7H17c-4.2 0-7-2.8-7-7V12c0-4.2 2.8-7 7-7z" fill="#df514d" />
      <path d="M45 5v10c0 3.9 2.2 6.1 6.1 6.1H54z" fill="#f1a09b" />
      <text x="17" y="39" fill="#ffffff" fontSize="15.5" fontWeight="900" fontFamily="Arial, sans-serif">
        PDF
      </text>
    </SvgIcon>
  );
}

const AdminSunatIcon = ({
  comprobante_key,            // ej. "01-F001-12345" pero es KEY del registro
  comprobante,            // ej. "01-F001-12345" pero es para mostrar links
  cdr_pendiente,          // '1' o '0' o null  NEWW
  elemento,               // tu valor de elemento
  firma,                  // string o null
  documentoId,            // params.documento_id
  periodoTrabajo,         // periodo_trabajo
  idAnfitrion,            // params.id_anfitrion
  contabilidadTrabajo,    // contabilidad_trabajo
  backHost,               // ej. "https://tu-backend.com"
  cpeEndpoint = "/ad_ventacpe",
  cpeRequestExtra = {},
  pdfEndpoint,
  pdfRequestExtra = {},
  generarPdfLocal,
  size = 24,              // tamaño del ícono
  cdr_nivel,                //ACEPTADO,RECHAZADO,PENDIENTE
  cdr_descripcion,
  numeroRdi,
  onRefresh,              // ✅ función opcional para refrescar al cerrar el modal
  descargasHost = "http://74.208.184.113:8080", // opcional, por si cambia el host
}) => {
  const [showModal, setShowModal] = useState(false);
  const [rutaXml, setRutaXml] = useState("");
  const [rutaCdr, setRutaCdr] = useState("");
  const [rutaPdf, setRutaPdf] = useState("");
  const [cargandoPdf, setCargandoPdf] = useState(false);
  const [modalResumenRdi, setModalResumenRdi] = useState(false);
  const { confirmDialog } = useDialog(); //unico dialogo

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [phone, setPhone] = useState("");
  const firmaValor = String(firma || "").trim();
  const esPdfLocal = typeof generarPdfLocal === "function";

  const generarRutaPdf = async ({ cod, serie, numero }) => {
    if (esPdfLocal) {
      return generarPdfLocal({ cod, serie, numero, elemento });
    }

    if (!pdfEndpoint) {
      const baseUrl = `${descargasHost}/descargas/${documentoId}`;
      return `${baseUrl}/${documentoId}-${cod}-${serie}-${numero}.pdf`;
    }

    const response = await axios.post(`${backHost}${pdfEndpoint}`, {
      periodo: periodoTrabajo,
      id_anfitrion: idAnfitrion,
      id_usuario: idAnfitrion,
      documento_id: contabilidadTrabajo,
      r_cod: cod,
      r_serie: serie,
      r_numero: numero,
      elemento,
      ...pdfRequestExtra,
    });

    if (!response.data?.ruta_pdf || response.data.ruta_pdf === "error") {
      throw new Error(response.data?.message || response.data?.respuesta_sunat_descripcion || "No se pudo generar el PDF.");
    }

    return response.data.ruta_pdf;
  };

  const obtenerErrorSunat = (errorOrData) => {
    const responseData = errorOrData?.response?.data || errorOrData?.data || errorOrData || {};
    const data = responseData?.error || responseData;
    const nivel = data?.nivel || "ERROR";

    const tituloPorNivel = {
      RECHAZADO: "Comprobante rechazado",
      PENDIENTE: "CDR pendiente",
      ERROR: "No se pudo enviar a SUNAT",
    };

    const mensaje =
      data?.mensaje_usuario ||
      data?.respuesta_sunat_descripcion ||
      data?.detalle_tecnico ||
      data?.message ||
      errorOrData?.message ||
      "SIN DETALLE";

    return {
      titulo: data?.titulo_usuario || tituloPorNivel[nivel] || tituloPorNivel.ERROR,
      mensaje,
      detalle: data?.detalle_tecnico,
    };
  };

  const mostrarErrorSunat = async (errorOrData, tituloFallback = "No se pudo enviar a SUNAT") => {
    const errorSunat = obtenerErrorSunat(errorOrData);
    const detalle = errorSunat.detalle && errorSunat.detalle !== errorSunat.mensaje
      ? `\n\nDetalle: ${errorSunat.detalle}`
      : "";

    await confirmDialog({
      title: errorSunat.titulo || tituloFallback,
      message: `${comprobante}\n${errorSunat.mensaje}${detalle}`,
      icon: "error",
      confirmText: "ACEPTAR",
    });
  };

  const handleSunat = async () => {
    try {
      //kEY PARA PROCESAMIENTO SUNAT
      //const [COD, SERIE, NUMERO] = (comprobante_key || "").split("-");
      //comprobante(0) PARA MOSTRAR LINKS
      const [COD0, SERIE0, NUMERO0] = (comprobante || "").split("-");

      // Si ya está firmado, solo mostrar links
      //Pero si tiene cdr_pendiente, enviar de nuevo, api lo maneja

      if (firmaValor.startsWith("RDI:") || numeroRdi) {
        const baseUrl = `${descargasHost}/descargas/${documentoId}`;
        setRutaXml("");
        setRutaCdr("");
        setRutaPdf("");
        setCargandoPdf(true);
        setModalResumenRdi(true);
        setShowModal(true);
        try {
          setRutaPdf(esPdfLocal
            ? await generarRutaPdf({ cod: COD0, serie: SERIE0, numero: NUMERO0 })
            : `${baseUrl}/${documentoId}-${COD0}-${SERIE0}-${NUMERO0}.pdf`);
        } finally {
          setCargandoPdf(false);
        }
        return;
      }

      if (firmaValor) {
        const baseUrl = `${descargasHost}/descargas/${documentoId}`;
        setRutaXml(`${baseUrl}/${documentoId}-${COD0}-${SERIE0}-${NUMERO0}.xml`);
        setRutaCdr(`${baseUrl}/R-${documentoId}-${COD0}-${SERIE0}-${NUMERO0}.xml`);
        setRutaPdf("");
        setCargandoPdf(true);
        setModalResumenRdi(false);
        setShowModal(true);
        try {
          setRutaPdf(esPdfLocal
            ? await generarRutaPdf({ cod: COD0, serie: SERIE0, numero: NUMERO0 })
            : `${baseUrl}/${documentoId}-${COD0}-${SERIE0}-${NUMERO0}.pdf`);
        } finally {
          setCargandoPdf(false);
        }
        return;
      }

      // Confirmación antes de enviar
      await enviaSunat();

    } catch (error) {
      await mostrarErrorSunat(error, "Error en procesamiento Interno");
    }

  };

  const enviaSunat = async () => {
      //kEY PARA PROCESAMIENTO SUNAT
      const [COD, SERIE, NUMERO] = (comprobante_key || "").split("-");
      //comprobante(0) PARA MOSTRAR LINKS
      //const [COD0, SERIE0, NUMERO0] = (comprobante || "").split("-");

      // Confirmacion antes de enviar
      const result = await confirmDialog({
        title: "Enviar a SUNAT?",
        message: `${comprobante}`,
        icon: "success",
        confirmText: "ENVIAR",
        cancelText: "CANCELAR",
      });

      if (!result.isConfirmed) return;

      try {
        const response = await axios.post(`${backHost}${cpeEndpoint}`, {
          p_periodo: periodoTrabajo,
          p_id_usuario: idAnfitrion,
          p_documento_id: contabilidadTrabajo,
          p_r_cod: COD,
          p_r_serie: SERIE,
          p_r_numero: NUMERO,
          p_elemento: elemento,
          ...cpeRequestExtra,
        });

        if (response.data?.codigo_hash) {
          setRutaXml(response.data.ruta_xml);
          setRutaCdr(response.data.ruta_cdr);
          setModalResumenRdi(false);
          setShowModal(true);
          if (pdfEndpoint || esPdfLocal) {
            setRutaPdf("");
            setCargandoPdf(true);
            try {
              setRutaPdf(await generarRutaPdf({ cod: COD, serie: SERIE, numero: NUMERO }));
            } finally {
              setCargandoPdf(false);
            }
          } else {
            setRutaPdf(response.data.ruta_pdf);
          }
          return;
        }

        await mostrarErrorSunat(response.data, "Error de envio SUNAT");
      }
      catch (error) {
        await mostrarErrorSunat(error, "Error de envio SUNAT");
      }
  };
  const enviaSunatReprocesoCDR = async () => {
      //kEY PARA PROCESAMIENTO SUNAT
      const [COD, SERIE, NUMERO] = (comprobante_key || "").split("-");
      //comprobante(0) PARA MOSTRAR LINKS
      //const [COD0, SERIE0, NUMERO0] = (comprobante || "").split("-");

      // Confirmacion antes de enviar
      const result = await confirmDialog({
        title: "Solicitar a Sunat Descarga CDR?",
        message: `${comprobante}`,
        icon: "success",
        confirmText: "ENVIAR",
        cancelText: "CANCELAR",
      });

      if (!result.isConfirmed) return;

      try {
        const response = await axios.post(`${backHost}${cpeEndpoint}`, {
          p_periodo: periodoTrabajo,
          p_id_usuario: idAnfitrion,
          p_documento_id: contabilidadTrabajo,
          p_r_cod: COD,
          p_r_serie: SERIE,
          p_r_numero: NUMERO,
          p_elemento: elemento,
          ...cpeRequestExtra,
        });

        //console.log('response del reproceso: ', response);
        if (response.data?.estado) {
          setRutaXml(response.data.ruta_xml);
          setRutaCdr(response.data.ruta_cdr);
          setRutaPdf(response.data.ruta_pdf);
          return;
        }

        await mostrarErrorSunat(response.data, "Error de solicitud CDR SUNAT");
      }
      catch (error) {
        await mostrarErrorSunat(error, "Error de solicitud CDR SUNAT");
      }
  };
  const handleOpenLink = async (url) => {
    if (cdr_pendiente === '1') {
      // Si el CDR está pendiente, intentar reprocesar
      if (url.includes('R-')) {
        //console.log('Sí contiene R-');
        await enviaSunatReprocesoCDR();
      }
    }
    if (!url) return;

    const urlConBypassCache = url.startsWith("blob:")
      ? url
      : `${url}?t=${Date.now()}`;

    window.open(urlConBypassCache, "_blank", "noopener,noreferrer");
  };

const handleOpenLinkWhatsApp = async (sNumero) => {
  if (cargandoPdf || !rutaPdf) return;
  // 📞 limpiar número
  let telefono = sNumero.replace(/\D/g, '');
  if (!telefono.startsWith('51')) {
    telefono = '51' + telefono;
  }
  const BACKEND_DESCARGAS = 'https://xpertcont-backend-js-production-50e6.up.railway.app';
  
  // 🔧 normalizar rutas
  const pathPdf = normalizarRutaDescarga(rutaPdf);
  const pathXml = normalizarRutaDescarga(rutaXml);
  const pathCdr = normalizarRutaDescarga(rutaCdr);

  // 🌍 nuevas rutas limpias
  const pdfFinal = `${BACKEND_DESCARGAS}${pathPdf}`;
  const xmlFinal = `${BACKEND_DESCARGAS}${pathXml}`;
  const cdrFinal = `${BACKEND_DESCARGAS}${pathCdr}`;

  // 💬 mensaje WhatsApp
  const mensaje =
    modalResumenRdi
      ? `expertcont.pe 👋\nTe comparte tu comprobante electrónico:\n\n📄 PDF:\n${pdfFinal}\n\nProcesado por Resumen Diario SUNAT ${numeroRdi || firmaValor.replace("RDI:", "")}.\nCopia y pega en tu navegador de preferencia.`
      : `expertcont.pe 👋\n` +
        `Te comparte tu comprobante electrónico:\n\n` +
        `📄 PDF:\n${pdfFinal}\n` +
        `📦 XML:\n${xmlFinal}\n` +
        `✅ CDR:\n${cdrFinal}\n\n` +
        `Copia y pega en tu navegador de preferencia.`;

  const waUrl = `https://wa.me/${telefono}?text=${encodeURIComponent(mensaje)}`;

  window.open(waUrl, '_blank');
};
  
  const normalizarRutaDescarga = (urlOriginal) => {
    if (!urlOriginal) return '';

    try {
      const url = new URL(urlOriginal);
      return url.pathname; // 👉 /descargas/20614126435/archivo.pdf
    } catch (error) {
      // fallback por si llega algo raro
      const index = urlOriginal.indexOf('/descargas/');
      return index !== -1 ? urlOriginal.substring(index) : '';
    }
  };

  const handleCloseModal = () => {
    // ✅ dispara el refresco si te pasaron la función
    if (onRefresh) onRefresh();
    setShowModal(false);
    setModalResumenRdi(false);
  };
  
  const getSunatIcon = () => {

    switch (cdr_nivel) {
        case "ACEPTADO":
            return Sunat01Icon;

        case "RECHAZADO":
            return Sunat03Icon;
        
        //Muy pronto :)
        //case "OBSERVADO":
        //    return SunatNaranja;

        default:
            return Sunat01Icon;
    }
};
  const whatsappListo = phone.length >= 9 && Boolean(rutaPdf) && !rutaPdf.startsWith("blob:") && !cargandoPdf;

  return (
    <>
      {/* Ícono */}
      <img
        src={getSunatIcon()}
        onClick={handleSunat}
        alt="Icono Sunat01"
        style={{
          cursor: "pointer",
          filter: firmaValor ? "grayscale(0)" : "grayscale(0.8)",
          transition: "color 0.3s ease",
          width: size,
          height: size,
        }}
      />

      {/* Modal interno */}
      <Dialog
        open={showModal}
        onClose={handleCloseModal}
        maxWidth="md"
        disableScrollLock
        PaperProps={{
          sx: {
            ...modalPaperSx,
            top: isSmallScreen ? "-20vh" : "0vh",
            left: isSmallScreen ? "0%" : "0%",
            mt: "10vh",
            width: isSmallScreen ? "min(360px, 70vw)" : 420,
          },
        }}
      >
        <DialogTitle
          sx={{
            width: "100%",
            py: 1.4,
            color: "rgba(255,255,255,0.94)",
            textAlign: "center",
            backgroundColor: palette.surfaceAlt,
            borderBottom: `1px solid ${palette.border}`,
          }}
        >
          <Box sx={{ display: "grid", justifyItems: "center", gap: 0.35 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, color: "#7ddbd3", fontSize: "14px", fontWeight: 800 }}>
              <TaskAltIcon sx={{ fontSize: 18 }} />
              {modalResumenRdi ? "Procesado por Resumen Diario" : "Envio exitoso"}
            </Box>
            <Typography sx={{ color: palette.muted, fontSize: "11.5px", fontWeight: 500 }}>
              {modalResumenRdi ? `RDI: ${numeroRdi || firmaValor.replace("RDI:", "")}` : "Comprobante procesado y documentos disponibles"}
            </Typography>
          </Box>
        </DialogTitle>

        <Typography sx={comprobanteInfoSx}>
          {comprobante}
        </Typography>

        <Typography sx={downloadInfoSx}>
          {modalResumenRdi
            ? (cdr_descripcion || "Este comprobante fue incluido en un Resumen Diario SUNAT. XML y CDR pertenecen al resumen.")
            : "Puede descargar o enviar estos links por WhatsApp."}
        </Typography>

        {!modalResumenRdi && (
          <Button
            variant="contained"
            onClick={() => handleOpenLink(rutaXml)}
            sx={{ ...xmlButtonSx, mt: 1.5 }}
            startIcon={<CodeIcon />} 
          >
            Descargar XML
          </Button>
        )}

        {!modalResumenRdi && (
          <Button
            variant="contained"
            onClick={() => handleOpenLink(rutaCdr)}
            sx={{ ...cdrButtonSx, mt: 1 }}
            startIcon={<TaskAltIcon />} 
          >
            Descargar CDR
          </Button>
        )}

        <Button
          variant="contained"
          onClick={() => handleOpenLink(rutaPdf)}
          disabled={cargandoPdf || !rutaPdf}
          sx={{ ...pdfButtonSx, mt: modalResumenRdi ? 1.5 : 1 }}
          startIcon={<PdfFileIcon sx={{ fontSize: 34 }} />}
        >
          {cargandoPdf ? "Generando PDF..." : "Descargar PDF"}
        </Button>

    <Box sx={{ position: "relative", width: 270, mt: 1 }}>
      {/* Centro: Icono + WSP */}
      <Box
        onClick={() => handleOpenLinkWhatsApp(phone)}
        sx={{
          position: "absolute",
          left: 16,
          top: "50%",
          transform: "translateY(-50%)",
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          color: whatsappListo ? "#7ddbd3" : palette.muted,
          px: 1.2,
          py: 0.4,
          borderRadius: 1,
          zIndex: 10,
          cursor: whatsappListo ? "pointer" : "default",
          opacity: whatsappListo ? 1 : 0.6,
          "&:hover": {
            backgroundColor:
              whatsappListo ? "rgba(42,161,152,0.14)" : "transparent"
          }
        }}
      >
        <WhatsAppIcon sx={{ fontSize: 23 }} />
      </Box>

      {/* Input */}
      <TextField
        size="small"
        placeholder="Teléfono"
        autoComplete="off"
        value={phone}
        onChange={(e) =>
          setPhone(e.target.value.replace(/\D/g, ""))
        }
        sx={{
          ...phoneFieldSx,
          "& input": {
            ...phoneFieldSx["& input"],
            paddingLeft: "48px",
            paddingRight: "16px",
          }
        }}
      />
    </Box>

        {/*
        <Button
          variant="contained"
          //color="info"
          onClick={() => handleOpenLinkWhatsApp('973586639')} //número de prueba
          sx={{ //display: "block", 
                display: "flex",          // 🔹 asegura layout en fila
                alignItems: "center",     // centra verticalmente
                //justifyContent: "flex-start", // texto alineado con el ícono            
                margin: ".5rem 0", 
                width: 270, 
                backgroundColor: "#374151", // color gris oscuro
                "&:hover": { backgroundColor: "#4B5563" }, // color gris más claro al pasar el mouse
                mt: -0.5, 
                fontWeight: "bold" }}
          startIcon={<WhatsAppIcon />}
        >
          WSP
        </Button>
        */} 

        <Button
          variant="contained"
          onClick={handleCloseModal}
          sx={{ ...closeButtonSx, my: 2 }}
        >
          ESC - CERRAR
        </Button>
      </Dialog>
    </>
  );
};

export default AdminSunatIcon;
